import type { Candle } from '../types';
import {
  clampFeePercent,
  grossPnlPercent,
  roundTripFeePercent,
} from '../strategies/common/fees';

type TradeSide = 'long' | 'short';

export interface OpenCode5mStrategyInputs {
  timeframe: '5m';
  makerFeePercent: number;
  takerFeePercent: number;
  allowLongs: boolean;
  allowShorts: boolean;
  fastEmaPeriod: number;
  slowEmaPeriod: number;
  rsiPeriod: number;
  rsiLongMin: number;
  rsiShortMax: number;
  atrPeriod: number;
  minTrendStrengthPercent: number;
  minRiskPercent: number;
  stopLossAtrMultiplier: number;
  takeProfitR: number;
  trailingActivationR: number;
  breakEvenR: number;
  trailingAtrMultiplier: number;
  maxHoldCandles: number;
  cooldownCandles: number;
  forceEntryAfterCandles: number;
  maxHistoryCandles: number;
}

export interface OpenCode5mStrategyTrade {
  side: TradeSide;
  entryIndex: number;
  entryPrice: number;
  entryTime: string;
  initialStopPrice: number;
  finalStopPrice: number;
  takeProfitPrice: number;
  exitIndex: number;
  exitPrice: number;
  exitTime: string;
  durationCandles: number;
  exitReason: 'stopLoss' | 'takeProfit' | 'timeExit' | 'signalFlip';
  grossPnlPercent: number;
  feePercent: number;
  pnlPercent: number;
}

export interface OpenCode5mStrategyMarker {
  index: number;
  action: 'entryLong' | 'entryShort' | 'exitLong' | 'exitShort';
  reason?: 'stopLoss' | 'takeProfit' | 'timeExit' | 'signalFlip';
}

export interface OpenCode5mStrategyStats {
  totalTrades: number;
  longTrades: number;
  shortTrades: number;
  takeProfitCount: number;
  stopLossCount: number;
  timeExitCount: number;
  signalFlipCount: number;
  tradesPerDay: number;
  winRate: number;
  totalPnlPercent: number;
  averagePnlPercent: number;
  totalGrossPnlPercent: number;
  totalFeesPercent: number;
}

export interface OpenCode5mStrategyActiveTrade {
  state: 'inTrade';
  side: TradeSide;
  entryIndex: number;
  entryPrice: number;
  stopPrice: number;
  takeProfitPrice: number;
}

export interface OpenCode5mStrategyOverlayResult {
  markers: OpenCode5mStrategyMarker[];
  trades: OpenCode5mStrategyTrade[];
  stats: OpenCode5mStrategyStats;
  activeTrade: OpenCode5mStrategyActiveTrade | null;
}

interface InternalTradeState {
  side: TradeSide;
  entryMode: 'trend' | 'forced';
  entryIndex: number;
  entryPrice: number;
  initialStopPrice: number;
  stopPrice: number;
  takeProfitPrice: number;
  riskPerUnit: number;
  maxHoldCandles: number;
  trailingActivationR: number;
  breakEvenR: number;
  highestHigh: number;
  lowestLow: number;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function clampPositive(value: number, fallback: number): number {
  if (!Number.isFinite(value) || value <= 0) {
    return fallback;
  }
  return value;
}

function emptyStats(): OpenCode5mStrategyStats {
  return {
    totalTrades: 0,
    longTrades: 0,
    shortTrades: 0,
    takeProfitCount: 0,
    stopLossCount: 0,
    timeExitCount: 0,
    signalFlipCount: 0,
    tradesPerDay: 0,
    winRate: 0,
    totalPnlPercent: 0,
    averagePnlPercent: 0,
    totalGrossPnlPercent: 0,
    totalFeesPercent: 0,
  };
}

function applyHistoryWindow(
  candles: Candle[],
  maxHistoryCandles: number | undefined,
): { candles: Candle[]; indexOffset: number } {
  if (maxHistoryCandles === undefined || !Number.isFinite(maxHistoryCandles)) {
    return { candles, indexOffset: 0 };
  }

  const cappedLength = Math.floor(maxHistoryCandles);
  if (cappedLength <= 0 || candles.length <= cappedLength) {
    return { candles, indexOffset: 0 };
  }

  const indexOffset = candles.length - cappedLength;
  return {
    candles: candles.slice(indexOffset),
    indexOffset,
  };
}

function computeEmaSeries(candles: Candle[], period: number): (number | undefined)[] {
  const normalizedPeriod = Math.max(1, Math.floor(period));
  const values: (number | undefined)[] = new Array(candles.length).fill(undefined);
  if (candles.length === 0) {
    return values;
  }

  const multiplier = 2 / (normalizedPeriod + 1);
  let rollingSum = 0;

  for (let index = 0; index < candles.length; index += 1) {
    const close = candles[index].close;
    rollingSum += close;

    if (index === normalizedPeriod - 1) {
      values[index] = rollingSum / normalizedPeriod;
      continue;
    }

    if (index >= normalizedPeriod) {
      const previous = values[index - 1];
      if (previous !== undefined) {
        values[index] = previous + (close - previous) * multiplier;
      }
    }
  }

  return values;
}

function computeRsiSeries(candles: Candle[], period: number): (number | undefined)[] {
  const normalizedPeriod = Math.max(2, Math.floor(period));
  const output: (number | undefined)[] = new Array(candles.length).fill(undefined);
  if (candles.length <= normalizedPeriod) {
    return output;
  }

  let gainSum = 0;
  let lossSum = 0;

  for (let index = 1; index <= normalizedPeriod; index += 1) {
    const change = candles[index].close - candles[index - 1].close;
    if (change >= 0) {
      gainSum += change;
    } else {
      lossSum += -change;
    }
  }

  let avgGain = gainSum / normalizedPeriod;
  let avgLoss = lossSum / normalizedPeriod;

  output[normalizedPeriod] =
    avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / Math.max(avgLoss, 1e-12));

  for (let index = normalizedPeriod + 1; index < candles.length; index += 1) {
    const change = candles[index].close - candles[index - 1].close;
    const gain = change > 0 ? change : 0;
    const loss = change < 0 ? -change : 0;

    avgGain = (avgGain * (normalizedPeriod - 1) + gain) / normalizedPeriod;
    avgLoss = (avgLoss * (normalizedPeriod - 1) + loss) / normalizedPeriod;

    output[index] =
      avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / Math.max(avgLoss, 1e-12));
  }

  return output;
}

function computeAtrSeries(candles: Candle[], period: number): (number | undefined)[] {
  const normalizedPeriod = Math.max(2, Math.floor(period));
  const output: (number | undefined)[] = new Array(candles.length).fill(undefined);
  if (candles.length <= normalizedPeriod) {
    return output;
  }

  const trueRanges: number[] = new Array(candles.length).fill(0);
  for (let index = 1; index < candles.length; index += 1) {
    const candle = candles[index];
    const previousClose = candles[index - 1].close;
    const highLow = candle.high - candle.low;
    const highClose = Math.abs(candle.high - previousClose);
    const lowClose = Math.abs(candle.low - previousClose);
    trueRanges[index] = Math.max(highLow, highClose, lowClose);
  }

  let sum = 0;
  for (let index = 1; index <= normalizedPeriod; index += 1) {
    sum += trueRanges[index];
  }

  let atr = sum / normalizedPeriod;
  output[normalizedPeriod] = atr;

  for (let index = normalizedPeriod + 1; index < candles.length; index += 1) {
    atr = (atr * (normalizedPeriod - 1) + trueRanges[index]) / normalizedPeriod;
    output[index] = atr;
  }

  return output;
}

function computeTrendStrengthPercent(close: number, fastEma: number, slowEma: number): number {
  if (!Number.isFinite(close) || close <= 0) {
    return 0;
  }
  return (Math.abs(fastEma - slowEma) / close) * 100;
}

function resolveLongExit(
  candle: Candle,
  stopPrice: number,
  takeProfitPrice: number,
): { reason: 'stopLoss' | 'takeProfit'; price: number } | null {
  if (candle.open <= stopPrice) {
    return { reason: 'stopLoss', price: candle.open };
  }
  if (candle.open >= takeProfitPrice) {
    return { reason: 'takeProfit', price: candle.open };
  }

  const stopTouched = candle.low <= stopPrice;
  const takeProfitTouched = candle.high >= takeProfitPrice;

  if (stopTouched && takeProfitTouched) {
    return { reason: 'stopLoss', price: stopPrice };
  }

  if (stopTouched) {
    return { reason: 'stopLoss', price: stopPrice };
  }
  if (takeProfitTouched) {
    return { reason: 'takeProfit', price: takeProfitPrice };
  }

  return null;
}

function resolveShortExit(
  candle: Candle,
  stopPrice: number,
  takeProfitPrice: number,
): { reason: 'stopLoss' | 'takeProfit'; price: number } | null {
  if (candle.open >= stopPrice) {
    return { reason: 'stopLoss', price: candle.open };
  }
  if (candle.open <= takeProfitPrice) {
    return { reason: 'takeProfit', price: candle.open };
  }

  const stopTouched = candle.high >= stopPrice;
  const takeProfitTouched = candle.low <= takeProfitPrice;

  if (stopTouched && takeProfitTouched) {
    return { reason: 'stopLoss', price: stopPrice };
  }

  if (stopTouched) {
    return { reason: 'stopLoss', price: stopPrice };
  }
  if (takeProfitTouched) {
    return { reason: 'takeProfit', price: takeProfitPrice };
  }

  return null;
}

function normalizeInputs(raw: OpenCode5mStrategyInputs): OpenCode5mStrategyInputs {
  return {
    timeframe: '5m',
    makerFeePercent: clampFeePercent(raw.makerFeePercent),
    takerFeePercent: clampFeePercent(raw.takerFeePercent),
    allowLongs: raw.allowLongs,
    allowShorts: raw.allowShorts,
    fastEmaPeriod: Math.round(clamp(raw.fastEmaPeriod, 2, 300)),
    slowEmaPeriod: Math.round(clamp(raw.slowEmaPeriod, 3, 400)),
    rsiPeriod: Math.round(clamp(raw.rsiPeriod, 2, 100)),
    rsiLongMin: clamp(raw.rsiLongMin, 0, 100),
    rsiShortMax: clamp(raw.rsiShortMax, 0, 100),
    atrPeriod: Math.round(clamp(raw.atrPeriod, 2, 200)),
    minTrendStrengthPercent: clamp(Math.max(0, raw.minTrendStrengthPercent), 0, 10),
    minRiskPercent: clamp(clampPositive(raw.minRiskPercent, 0.05), 0.01, 10),
    stopLossAtrMultiplier: clamp(clampPositive(raw.stopLossAtrMultiplier, 1), 0.1, 20),
    takeProfitR: clamp(clampPositive(raw.takeProfitR, 1), 0.1, 20),
    trailingActivationR: clamp(clampPositive(raw.trailingActivationR, 0.5), 0.1, 20),
    breakEvenR: clamp(clampPositive(raw.breakEvenR, 0.8), 0.1, 20),
    trailingAtrMultiplier: clamp(clampPositive(raw.trailingAtrMultiplier, 1), 0.1, 20),
    maxHoldCandles: Math.round(clamp(raw.maxHoldCandles, 1, 5000)),
    cooldownCandles: Math.round(clamp(raw.cooldownCandles, 0, 5000)),
    forceEntryAfterCandles: Math.round(clamp(raw.forceEntryAfterCandles, 1, 5000)),
    maxHistoryCandles: Math.round(clamp(raw.maxHistoryCandles, 200, 200000)),
  };
}

export function computeOpenCode5mStrategyOverlay(
  candles: Candle[],
  _overlay: unknown,
  rawInputs: OpenCode5mStrategyInputs,
  maxHistoryCandles?: number,
): OpenCode5mStrategyOverlayResult {
  const markers: OpenCode5mStrategyMarker[] = [];
  const trades: OpenCode5mStrategyTrade[] = [];
  const stats = emptyStats();

  if (candles.length === 0) {
    return {
      markers,
      trades,
      stats,
      activeTrade: null,
    };
  }

  const normalizedInputs = normalizeInputs(rawInputs);
  const historyCap =
    maxHistoryCandles !== undefined
      ? maxHistoryCandles
      : normalizedInputs.maxHistoryCandles;
  const { candles: strategyCandles, indexOffset } = applyHistoryWindow(candles, historyCap);

  if (strategyCandles.length < 20) {
    return {
      markers,
      trades,
      stats,
      activeTrade: null,
    };
  }

  const fastEma = computeEmaSeries(strategyCandles, normalizedInputs.fastEmaPeriod);
  const slowEma = computeEmaSeries(strategyCandles, normalizedInputs.slowEmaPeriod);
  const rsi = computeRsiSeries(strategyCandles, normalizedInputs.rsiPeriod);
  const atr = computeAtrSeries(strategyCandles, normalizedInputs.atrPeriod);

  const warmup = Math.max(
    normalizedInputs.slowEmaPeriod,
    normalizedInputs.rsiPeriod,
    normalizedInputs.atrPeriod,
  );
  const perTradeFeePercent = roundTripFeePercent(
    normalizedInputs.makerFeePercent,
    normalizedInputs.takerFeePercent,
  );

  let activeTrade: InternalTradeState | null = null;
  let profitableTrades = 0;
  let cooldownRemaining = 0;
  let candlesWithoutEntry = 0;

  for (let index = 1; index < strategyCandles.length; index += 1) {
    const candle = strategyCandles[index];
    const close = candle.close;
    const previousCandle = strategyCandles[index - 1];

    const fastValue = fastEma[index];
    const slowValue = slowEma[index];
    const rsiValue = rsi[index];
    const atrValue = atr[index];
    const previousFast = fastEma[index - 1];
    const previousSlow = slowEma[index - 1];
    const previousRsi = rsi[index - 1];
    const slowSlopeLookbackIndex = Math.max(0, index - 12);
    const slowSlopeBase = slowEma[slowSlopeLookbackIndex];
    const canEvaluateSignal =
      index >= warmup &&
      fastValue !== undefined &&
      slowValue !== undefined &&
      rsiValue !== undefined &&
      atrValue !== undefined;

    const trendStrengthPercent =
      canEvaluateSignal && fastValue !== undefined && slowValue !== undefined
        ? computeTrendStrengthPercent(close, fastValue, slowValue)
        : 0;

    const atrPercent =
      canEvaluateSignal && atrValue !== undefined && close > 0 ? (atrValue / close) * 100 : 0;
    const slowSlopePercent =
      canEvaluateSignal && slowSlopeBase !== undefined && close > 0
        ? ((slowValue! - slowSlopeBase) / close) * 100
        : 0;
    const isHighVolatility = canEvaluateSignal && atrPercent > Math.max(1.4, normalizedInputs.minRiskPercent * 2.2);
    const isTrendRegime =
      canEvaluateSignal &&
      trendStrengthPercent >= normalizedInputs.minTrendStrengthPercent &&
      Math.abs(slowSlopePercent) >= normalizedInputs.minTrendStrengthPercent * 0.25;

    const longCrossUp =
      canEvaluateSignal &&
      previousFast !== undefined &&
      previousSlow !== undefined &&
      previousFast <= previousSlow &&
      fastValue! > slowValue!;
    const longPullbackRecover =
      canEvaluateSignal && previousFast !== undefined && previousCandle.close < previousFast && close >= fastValue!;
    const longRsiCrossUp =
      canEvaluateSignal &&
      previousRsi !== undefined &&
      previousRsi < normalizedInputs.rsiLongMin &&
      rsiValue! >= normalizedInputs.rsiLongMin;

    const shortCrossDown =
      canEvaluateSignal &&
      previousFast !== undefined &&
      previousSlow !== undefined &&
      previousFast >= previousSlow &&
      fastValue! < slowValue!;
    const shortPullbackRecover =
      canEvaluateSignal && previousFast !== undefined && previousCandle.close > previousFast && close <= fastValue!;
    const shortRsiCrossDown =
      canEvaluateSignal &&
      previousRsi !== undefined &&
      previousRsi > normalizedInputs.rsiShortMax &&
      rsiValue! <= normalizedInputs.rsiShortMax;

    const longTrendSignal =
      canEvaluateSignal &&
      normalizedInputs.allowLongs &&
      !isHighVolatility &&
      isTrendRegime &&
      slowSlopePercent >= 0 &&
      fastValue! > slowValue! &&
      close >= fastValue! &&
      rsiValue! >= normalizedInputs.rsiLongMin &&
      (longCrossUp || longPullbackRecover || longRsiCrossUp);

    const shortTrendSignal =
      canEvaluateSignal &&
      normalizedInputs.allowShorts &&
      !isHighVolatility &&
      isTrendRegime &&
      slowSlopePercent <= 0 &&
      fastValue! < slowValue! &&
      close <= fastValue! &&
      rsiValue! <= normalizedInputs.rsiShortMax &&
      (shortCrossDown || shortPullbackRecover || shortRsiCrossDown);

    if (activeTrade) {
      const currentAtr = atrValue ?? Math.max(candle.high - candle.low, activeTrade.entryPrice * 0.0005);

      let exitReason: OpenCode5mStrategyTrade['exitReason'] | null = null;
      let exitPrice: number | null = null;

      if (activeTrade.side === 'long') {
        activeTrade.highestHigh = Math.max(activeTrade.highestHigh, candle.high);
        activeTrade.lowestLow = Math.min(activeTrade.lowestLow, candle.low);

        const moveR = (activeTrade.highestHigh - activeTrade.entryPrice) / activeTrade.riskPerUnit;
        if (moveR >= activeTrade.breakEvenR) {
          activeTrade.stopPrice = Math.max(activeTrade.stopPrice, activeTrade.entryPrice);
        }
        if (moveR >= activeTrade.trailingActivationR) {
          const trailCandidate =
            activeTrade.highestHigh - currentAtr * normalizedInputs.trailingAtrMultiplier;
          activeTrade.stopPrice = Math.max(activeTrade.stopPrice, trailCandidate);
        }

        const directExit = resolveLongExit(candle, activeTrade.stopPrice, activeTrade.takeProfitPrice);
        if (directExit) {
          exitReason = directExit.reason;
          exitPrice = directExit.price;
        }
      } else {
        activeTrade.highestHigh = Math.max(activeTrade.highestHigh, candle.high);
        activeTrade.lowestLow = Math.min(activeTrade.lowestLow, candle.low);

        const moveR = (activeTrade.entryPrice - activeTrade.lowestLow) / activeTrade.riskPerUnit;
        if (moveR >= activeTrade.breakEvenR) {
          activeTrade.stopPrice = Math.min(activeTrade.stopPrice, activeTrade.entryPrice);
        }
        if (moveR >= activeTrade.trailingActivationR) {
          const trailCandidate =
            activeTrade.lowestLow + currentAtr * normalizedInputs.trailingAtrMultiplier;
          activeTrade.stopPrice = Math.min(activeTrade.stopPrice, trailCandidate);
        }

        const directExit = resolveShortExit(candle, activeTrade.stopPrice, activeTrade.takeProfitPrice);
        if (directExit) {
          exitReason = directExit.reason;
          exitPrice = directExit.price;
        }
      }

      if (!exitReason) {
        const durationCandles = Math.max(1, index - activeTrade.entryIndex);
        if (durationCandles >= activeTrade.maxHoldCandles) {
          exitReason = 'timeExit';
          exitPrice = close;
        } else if (
          (activeTrade.side === 'long' && shortTrendSignal) ||
          (activeTrade.side === 'short' && longTrendSignal)
        ) {
          exitReason = 'signalFlip';
          exitPrice = close;
        }
      }

      if (exitReason && exitPrice !== null) {
        const gross = grossPnlPercent(activeTrade.entryPrice, exitPrice, activeTrade.side);
        const net = gross - perTradeFeePercent;
        const durationCandles = Math.max(1, index - activeTrade.entryIndex);

        trades.push({
          side: activeTrade.side,
          entryIndex: activeTrade.entryIndex,
          entryPrice: activeTrade.entryPrice,
          entryTime: strategyCandles[activeTrade.entryIndex].time,
          initialStopPrice: activeTrade.initialStopPrice,
          finalStopPrice: activeTrade.stopPrice,
          takeProfitPrice: activeTrade.takeProfitPrice,
          exitIndex: index,
          exitPrice,
          exitTime: candle.time,
          durationCandles,
          exitReason,
          grossPnlPercent: gross,
          feePercent: perTradeFeePercent,
          pnlPercent: net,
        });

        markers.push({
          index,
          action: activeTrade.side === 'long' ? 'exitLong' : 'exitShort',
          reason: exitReason,
        });

        stats.totalTrades += 1;
        if (activeTrade.side === 'long') {
          stats.longTrades += 1;
        } else {
          stats.shortTrades += 1;
        }
        if (exitReason === 'takeProfit') {
          stats.takeProfitCount += 1;
        } else if (exitReason === 'stopLoss') {
          stats.stopLossCount += 1;
        } else if (exitReason === 'timeExit') {
          stats.timeExitCount += 1;
        } else {
          stats.signalFlipCount += 1;
        }

        stats.totalGrossPnlPercent += gross;
        stats.totalFeesPercent += perTradeFeePercent;
        stats.totalPnlPercent += net;

        if (net > 0) {
          profitableTrades += 1;
        }

        activeTrade = null;
        cooldownRemaining = normalizedInputs.cooldownCandles;
      }

      continue;
    }

    if (cooldownRemaining > 0) {
      cooldownRemaining -= 1;
      candlesWithoutEntry += 1;
      continue;
    }

    const forceEntry = candlesWithoutEntry >= normalizedInputs.forceEntryAfterCandles;
    let entrySide: TradeSide | null = null;
    let entryMode: InternalTradeState['entryMode'] | null = null;

    if (longTrendSignal) {
      entrySide = 'long';
      entryMode = 'trend';
    } else if (shortTrendSignal) {
      entrySide = 'short';
      entryMode = 'trend';
    } else if (forceEntry && canEvaluateSignal) {
      const lookbackIndex = Math.max(0, index - 12);
      const momentum = close - strategyCandles[lookbackIndex].close;

      if (
        fastValue! > slowValue! &&
        slowSlopePercent > 0 &&
        normalizedInputs.allowLongs &&
        rsiValue! >= Math.max(45, normalizedInputs.rsiLongMin - 6)
      ) {
        entrySide = 'long';
      } else if (
        fastValue! < slowValue! &&
        slowSlopePercent < 0 &&
        normalizedInputs.allowShorts &&
        rsiValue! <= Math.min(55, normalizedInputs.rsiShortMax + 6)
      ) {
        entrySide = 'short';
      } else if (momentum >= 0 && normalizedInputs.allowLongs) {
        entrySide = 'long';
      } else if (momentum < 0 && normalizedInputs.allowShorts) {
        entrySide = 'short';
      } else if (normalizedInputs.allowLongs) {
        entrySide = 'long';
      } else if (normalizedInputs.allowShorts) {
        entrySide = 'short';
      }

      if (entrySide) {
        entryMode = 'forced';
      }
    }

    if (!entrySide || !entryMode) {
      candlesWithoutEntry += 1;
      continue;
    }

    const baseAtr = atrValue ?? Math.max(candle.high - candle.low, close * 0.0005);
    const minRiskValue = close * (normalizedInputs.minRiskPercent / 100);
    const stopAtrMultiplier =
      entryMode === 'forced'
        ? Math.max(0.45, normalizedInputs.stopLossAtrMultiplier * 0.8)
        : normalizedInputs.stopLossAtrMultiplier;
    const unclampedRiskPerUnit = Math.max(
      minRiskValue,
      baseAtr * stopAtrMultiplier,
      close * 0.0002,
    );
    const maxRiskPercent = clamp(normalizedInputs.minRiskPercent * 3.2, 0.35, 2.5);
    const riskPerUnit = Math.min(unclampedRiskPerUnit, close * (maxRiskPercent / 100));
    const effectiveTakeProfitR =
      entryMode === 'forced'
        ? Math.min(normalizedInputs.takeProfitR, 1.2)
        : normalizedInputs.takeProfitR;
    const effectiveMaxHoldCandles =
      entryMode === 'forced'
        ? Math.max(8, Math.round(normalizedInputs.maxHoldCandles * 0.6))
        : normalizedInputs.maxHoldCandles;
    const effectiveTrailingActivationR =
      entryMode === 'forced'
        ? Math.min(normalizedInputs.trailingActivationR, 1)
        : normalizedInputs.trailingActivationR;
    const effectiveBreakEvenR =
      entryMode === 'forced'
        ? Math.min(normalizedInputs.breakEvenR, 0.9)
        : normalizedInputs.breakEvenR;
    const plannedRewardPercent =
      close > 0 ? ((riskPerUnit * effectiveTakeProfitR) / close) * 100 : 0;

    if (plannedRewardPercent <= perTradeFeePercent * 1.5) {
      candlesWithoutEntry += 1;
      continue;
    }

    let initialStopPrice = 0;
    let takeProfitPrice = 0;

    if (entrySide === 'long') {
      initialStopPrice = close - riskPerUnit;
      takeProfitPrice = close + riskPerUnit * effectiveTakeProfitR;
    } else {
      initialStopPrice = close + riskPerUnit;
      takeProfitPrice = close - riskPerUnit * effectiveTakeProfitR;
    }

    if (!Number.isFinite(initialStopPrice) || !Number.isFinite(takeProfitPrice) || takeProfitPrice <= 0) {
      candlesWithoutEntry += 1;
      continue;
    }

    activeTrade = {
      side: entrySide,
      entryMode,
      entryIndex: index,
      entryPrice: close,
      initialStopPrice,
      stopPrice: initialStopPrice,
      takeProfitPrice,
      riskPerUnit,
      maxHoldCandles: effectiveMaxHoldCandles,
      trailingActivationR: effectiveTrailingActivationR,
      breakEvenR: effectiveBreakEvenR,
      highestHigh: candle.high,
      lowestLow: candle.low,
    };

    markers.push({
      index,
      action: entrySide === 'long' ? 'entryLong' : 'entryShort',
    });
    candlesWithoutEntry = 0;
  }

  if (stats.totalTrades > 0) {
    stats.winRate = (profitableTrades / stats.totalTrades) * 100;
    stats.averagePnlPercent = stats.totalPnlPercent / stats.totalTrades;
  }

  const startMs = Date.parse(strategyCandles[0].time);
  const endMs = Date.parse(strategyCandles[strategyCandles.length - 1].time);
  if (Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs) {
    const days = (endMs - startMs) / 86_400_000;
    stats.tradesPerDay = days > 0 ? stats.totalTrades / days : 0;
  }

  let resolvedActiveTrade: OpenCode5mStrategyActiveTrade | null = null;
  if (activeTrade) {
    resolvedActiveTrade = {
      state: 'inTrade',
      side: activeTrade.side,
      entryIndex: activeTrade.entryIndex,
      entryPrice: activeTrade.entryPrice,
      stopPrice: activeTrade.stopPrice,
      takeProfitPrice: activeTrade.takeProfitPrice,
    };
  }

  if (indexOffset > 0) {
    markers.forEach((marker) => {
      marker.index += indexOffset;
    });

    trades.forEach((trade) => {
      trade.entryIndex += indexOffset;
      trade.exitIndex += indexOffset;
    });

    if (resolvedActiveTrade) {
      resolvedActiveTrade = {
        ...resolvedActiveTrade,
        entryIndex: resolvedActiveTrade.entryIndex + indexOffset,
      };
    }
  }

  return {
    markers,
    trades,
    stats,
    activeTrade: resolvedActiveTrade,
  };
}
